import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";
import { actions } from "../../redux/slice";
import { selectFormData, selectIsLoading, selectIsAddModalOpen } from "../../redux/selector";
import { newUserForm, DEPARTMENT_OPTIONS } from "../../types";
import { useStyles } from "./style";

type FieldConfig = {
  key: keyof newUserForm;
  label: string;
  type: "text" | "select";
  inputMode?: "text" | "numeric" | "email";
  placeholderKey?: string;
  values?: { value: string; labelKey: string }[];
};

const FIELDS: FieldConfig[] = [
  { key: "fullName", label: "FULL_NAME", type: "text" },
  { key: "email", label: "EMAIL", type: "text", inputMode: "email" },
  { key: "role", label: "ROLE", type: "text" },
  { key: "mobileNumber", label: "MOBILE_NUMBER", type: "text", inputMode: "numeric" },
  { key: "department", label: "DEPARTMENT", type: "select", placeholderKey: "SELECT_DEPARTMENT", values: DEPARTMENT_OPTIONS },
];

const REQUIRED_FIELDS: Array<keyof newUserForm> = ["fullName", "email", "role"];

const FORM = "ADMIN_MANAGEMENT.FORM";

const AddUserModal = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();

  const form = useSelector(selectFormData)!;
  const isLoading = useSelector(selectIsLoading);
  const isOpen = useSelector(selectIsAddModalOpen);

  const onClose = () => {
    if (isLoading) return;
    dispatch(actions.resetForm());
    dispatch(actions.closeAddModal());
  };

  const setField = (name: keyof newUserForm, value: string) => {
    const nextValue =
      name === "mobileNumber"
        ? valueForOnlyNumericInputTextFields(value)
        : name === "fullName" || name === "role"
          ? valueForOnlyStringInputTextFields(value)
          : value;

    dispatch(actions.setFormField({ name, value: nextValue }));
  };

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key]?.trim()));

  const onSubmit = () => {
    dispatch(actions.addNewUser(form));
    dispatch(actions.closeAddModal());
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box className={styles.modal} sx={{ direction: i18n.dir() }}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${FORM}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
        {FIELDS.map(({ key, label, type, values, inputMode, placeholderKey }) => (
          <Box key={key} className={styles.field}>
            <Typography className={styles.label}>
              {t(`${FORM}.${label}`)}
            </Typography>
            <TextField
              className={styles.input}
              fullWidth
              select={type === "select"}
              inputMode={inputMode}
              {...(type === "select"
                ? {
                    SelectProps: {
                      displayEmpty: true,
                      IconComponent: DropdownIcon,
                      renderValue: (selected) => {
                        const index = values?.findIndex((option) => option.value === selected) ?? -1;
                        return index > -1 ? (
                          t(values![index].labelKey)
                        ) : (
                          <span className={styles.selectPlaceholder}>
                            {t(`${FORM}.${placeholderKey}`)}
                          </span>
                        );
                      },
                    },
                  }
                : {})}
              variant="outlined"
              placeholder={t(`${FORM}.${label}`)}
              value={form[key]}
              disabled={isLoading}
              InputProps={{ notched: false }}
              onChange={(e) => setField(key, e.target.value)}
            >
              {values?.map((option) => (
                <MenuItem key={option.value} value={option.value} className={styles.selectMenuItem}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || isLoading}
            onClick={onSubmit}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddUserModal;
